"use strict";

/**
 * QBase 공통 어시스턴트 서비스 클라이언트 — 도메인 지식 없음.
 *
 * 턴 실행만 SSE 스트림이고 나머지는 평범한 JSON 이다. 턴은 POST 로 시작하므로 EventSource 를
 * 쓸 수 없다 — fetch 의 ReadableStream 을 직접 읽어 SSE 프레임을 푼다.
 *
 * 원본: openflow `services/assistantClient.ts` (LiveSQL `.js` 의 TypeScript 판).
 * 여기로 올리면서 바꾼 것은 **설정을 주입받는 것** 하나다 — 예전에는 `config/assistant.ts` 를
 * import 해서 시스템마다 사본이 필요했다.
 */
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createAssistantClient = exports.AssistantError = void 0;
class AssistantError extends Error {
  constructor(message, status, errorType = '') {
    super(message);
    this.name = 'AssistantError';
    this.status = status;
    this.errorType = errorType;
  }
}
exports.AssistantError = AssistantError;
/**
 * 설정 하나로 묶인 클라이언트를 만든다.
 * 앱은 부팅 때 한 번 만들어 훅에 넘긴다.
 */
function createAssistantClient(cfg) {
  const base = (cfg.baseUrl || '').replace(/\/+$/, '');
  const mount = (cfg.basePath || '/assistant').replace(/\/+$/, '');
  const root = `${base}${mount}`;
  const httpError = status => cfg.formatHttpError ? cfg.formatHttpError(status) : `요청이 실패했습니다 (HTTP ${status})`;
  /** 테넌트가 없는 백엔드(LiveSQL 자체판)에는 싣지 않는다 */
  const tenantQuery = cfg.tenantId ? `?tenantId=${encodeURIComponent(cfg.tenantId)}` : '';
  const tenantBody = cfg.tenantId ? {
    tenantId: cfg.tenantId
  } : {};
  async function call(path, init) {
    const res = await fetch(`${root}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      ...init
    });
    const text = await res.text();
    let body = null;
    try {
      body = text ? JSON.parse(text) : null;
    } catch {
      body = text;
    }
    if (!res.ok) {
      const msg = body && (body.error || body.message) || httpError(res.status);
      throw new AssistantError(msg, res.status, body && body.errorType || '');
    }
    return body;
  }
  return {
    config: cfg,
    // ---- 계정 ----
    getAccount: (provider = 'claude') => call(`/account?provider=${encodeURIComponent(provider)}`),
    startLogin: (provider = 'claude') => call('/login/start', {
      method: 'POST',
      body: JSON.stringify({
        provider
      })
    }),
    submitLoginCode: (loginId, code) => call('/login/code', {
      method: 'POST',
      body: JSON.stringify({
        loginId,
        ...(code ? {
          code
        } : {})
      })
    }),
    cancelLogin: () => call('/login/cancel', {
      method: 'POST',
      body: '{}'
    }),
    logout: (provider = 'claude') => call('/login/logout', {
      method: 'POST',
      body: JSON.stringify({
        provider
      })
    }),
    saveToken: (token, provider = 'claude') => call('/credential', {
      method: 'PUT',
      body: JSON.stringify({
        token,
        provider
      })
    }),
    removeToken: (provider = 'claude') => call(`/credential?provider=${encodeURIComponent(provider)}`, {
      method: 'DELETE'
    }),
    /** 개인 API 키 저장 — 브라우저에 두지 않고 서버가 암호화해 보관한다 */
    saveApiKey: (apiKey, provider = 'claude') => call('/credential', {
      method: 'PUT',
      body: JSON.stringify({
        token: apiKey,
        provider,
        kind: 'apikey'
      })
    }),
    removeApiKey: (provider = 'claude') => call(`/credential?provider=${encodeURIComponent(provider)}&kind=apikey`, {
      method: 'DELETE'
    }),
    // ---- 단발 작업 (대화·도구 없이 구조화된 JSON) ----
    // 공통 서비스 전용이다 — 테넌트 선언의 `tasks` 를 부른다.
    runTask: (taskId, input, opts = {}) => {
      if (!cfg.tenantId) {
        return Promise.reject(new AssistantError('단발 작업은 tenantId 가 설정된 공통 서비스에서만 쓸 수 있습니다.', 400, 'NO_TENANT'));
      }
      return call(`/tasks/${encodeURIComponent(cfg.tenantId)}/${encodeURIComponent(taskId)}`, {
        method: 'POST',
        body: JSON.stringify({
          input,
          ...opts
        })
      });
    },
    // ---- 스레드 ----
    // 목록·생성은 **봉투를 벗겨서** 돌려준다 — 호출부가 `r.threads`/`r.thread` 를 매번 푸는 것보다 낫다.
    listThreads: () => call(`/threads${tenantQuery}`).then(r => r.threads),
    createThread: (body = {}) => call('/threads', {
      method: 'POST',
      body: JSON.stringify({
        ...tenantBody,
        ...body
      })
    }).then(r => r.thread),
    getThread: id => call(`/threads/${id}`),
    deleteThread: id => call(`/threads/${id}`, {
      method: 'DELETE'
    }),
    answerQuestion: (threadId, questionId, answer) => call(`/threads/${threadId}/questions/${questionId}/answer`, {
      method: 'POST',
      body: JSON.stringify({
        answer
      })
    }),
    /**
     * 클라이언트 도구 결과 — 서비스가 SSE `client_tool`(callId·name·input)로 넘긴 도구를 앱이 자기
     * 화면에서 실행한 뒤 돌려준다. 테넌트 선언에 `client: true` 도구가 있는 앱(데이터모델러)만 쓴다.
     */
    submitToolResult: (threadId, callId, result) => call(`/threads/${threadId}/tools/${encodeURIComponent(callId)}/result`, {
      method: 'POST',
      body: JSON.stringify({
        result
      })
    }),
    abortTurn: threadId => call(`/threads/${threadId}/abort`, {
      method: 'POST',
      body: '{}'
    }),
    /**
     * 턴 실행. 이벤트가 올 때마다 onEvent 를 부르고 스트림이 끝나면 resolve 한다.
     * 사전 검사에 걸리면(로그인 없음·동시 실행 한도 등) 스트림 대신 JSON 오류가 오므로 던진다.
     */
    async runTurn(threadId, text, opts) {
      const res = await fetch(`${root}/threads/${threadId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream'
        },
        // authKind: 어떤 자격증명으로 돌릴지 화면이 고른 값('apikey' 등). 없으면 서버가 로그인 → 토큰 → 등록된 키 순으로 고른다.
        body: JSON.stringify({
          text,
          context: opts.context || {},
          ...(opts.provider ? {
            provider: opts.provider
          } : {}),
          ...(opts.model ? {
            model: opts.model
          } : {}),
          ...(opts.authKind ? {
            authKind: opts.authKind
          } : {})
        }),
        signal: opts.signal
      });
      if (!res.ok || !res.body || !(res.headers.get('content-type') || '').includes('text/event-stream')) {
        const raw = await res.text();
        let body = null;
        try {
          body = raw ? JSON.parse(raw) : null;
        } catch {
          body = raw;
        }
        const msg = body && (body.error || body.message) || httpError(res.status);
        throw new AssistantError(msg, res.status, body && body.errorType || '');
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // SSE 프레임은 빈 줄로 끊긴다. 서버가 보내는 주석(': ping')은 프레임을 유지하기만 하므로 버린다.
      for (;;) {
        const {
          value,
          done
        } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, {
          stream: true
        });
        let sep = buf.indexOf('\n\n');
        while (sep !== -1) {
          const frame = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          const ev = parseFrame(frame);
          if (ev) opts.onEvent(ev);
          sep = buf.indexOf('\n\n');
        }
      }
    }
  };
}
exports.createAssistantClient = createAssistantClient;
function parseFrame(frame) {
  let type = 'message';
  const dataLines = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // 주석(하트비트)
    if (line.startsWith('event:')) type = line.slice(6).trim();else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  if (!dataLines.length) return null;
  const raw = dataLines.join('\n');
  try {
    return {
      type,
      data: JSON.parse(raw)
    };
  } catch {
    return {
      type,
      data: raw
    };
  }
}
//# sourceMappingURL=client.js.map